import { Controller } from "@hotwired/stimulus"
import { computePosition, autoUpdate, flip, shift, offset } from "@floating-ui/dom"

// Dropdown popover controller using Floating UI for positioning
// Based on Rails Blocks dropdown pattern
export default class extends Controller {
  static targets = ["button", "menu", "template"]
  static values = {
    autoClose: { type: Boolean, default: true },
    nested: { type: Boolean, default: false },
    hover: { type: Boolean, default: false },
    autoPosition: { type: Boolean, default: true },
    lazyLoad: { type: Boolean, default: false },
    placement: { type: String, default: "bottom-end" },
    allowFlip: { type: Boolean, default: true },
    // "absolute" (default) positions relative to the nearest positioned ancestor and is
    // clipped by any `overflow: hidden` ancestor (e.g. a bordered table container);
    // "fixed" positions relative to the viewport so the menu floats over the page like a
    // popup and never gets clipped. Floating UI's autoUpdate keeps it glued on scroll.
    strategy: { type: String, default: "absolute" }
  }

  connect() {
    this.isOpen = false
    this.cleanup = null
    
    // Bind methods for event listeners
    this.handleClickOutside = this.handleClickOutside.bind(this)
    this.handleKeydown = this.handleKeydown.bind(this)
  }

  disconnect() {
    this.close()
    if (this.cleanup) {
      this.cleanup()
    }
  }

  toggle(event) {
    event.preventDefault()
    event.stopPropagation()
    
    if (this.isOpen) {
      this.close()
    } else {
      this.open()
    }
  }

  open() {
    if (this.isOpen) return
    
    // Lazy load content if configured
    if (this.lazyLoadValue && this.hasTemplateTarget && !this.menuTarget.innerHTML.trim()) {
      this.menuTarget.innerHTML = this.templateTarget.innerHTML
    }
    
    this.isOpen = true
    // Use show() instead of showModal() to allow click-outside detection
    this.menuTarget.show()
    
    // Position the menu using Floating UI
    if (this.autoPositionValue) {
      this.positionMenu()
    }
    
    // Add event listeners after a microtask to avoid the opening click triggering close.
    // Use mousedown so the outside-click closes immediately, before any sibling
    // dropdown's toggle click fires (which would otherwise re-open this one).
    requestAnimationFrame(() => {
      document.addEventListener("mousedown", this.handleClickOutside, true)
      document.addEventListener("keydown", this.handleKeydown)
    })
    
    // Dispatch custom event
    this.dispatch("opened")
  }

  close() {
    if (!this.isOpen) return
    
    this.isOpen = false
    this.menuTarget.close()
    
    // Remove event listeners
    document.removeEventListener("mousedown", this.handleClickOutside, true)
    document.removeEventListener("keydown", this.handleKeydown)
    
    // Cleanup Floating UI auto-update
    if (this.cleanup) {
      this.cleanup()
      this.cleanup = null
    }
    
    // Dispatch custom event
    this.dispatch("closed")
  }

  positionMenu() {
    const button = this.buttonTarget
    const menu = this.menuTarget

    // Match the element's CSS position to the Floating UI strategy. The menu's base
    // class is `absolute`; setting the inline style to `fixed` overrides it so the menu
    // escapes any `overflow: hidden` ancestor and floats over the page (e.g. the
    // bordered DataList table container). Inline styles win over the utility class.
    menu.style.position = this.strategyValue

    // Use autoUpdate to reposition on scroll/resize
    const middleware = [offset(4)]
    if (this.allowFlipValue) {
      middleware.push(flip({ fallbackPlacements: ["top-end", "bottom-start", "top-start"] }))
    }
    middleware.push(shift({ padding: 8 }))

    this.cleanup = autoUpdate(button, menu, () => {
      computePosition(button, menu, {
        placement: this.placementValue,
        strategy: this.strategyValue,
        middleware
      }).then(({ x, y }) => {
        Object.assign(menu.style, {
          left: `${x}px`,
          top: `${y}px`
        })
      })
    })
  }

  handleClickOutside(event) {
    // Don't close if clicking inside the menu
    if (this.menuTarget.contains(event.target)) return
    // Don't close if clicking the button (toggle handles that)
    if (this.buttonTarget.contains(event.target)) return
    
    this.close()
  }

  handleKeydown(event) {
    if (event.key === "Escape") {
      event.preventDefault()
      this.close()
      this.buttonTarget.focus()
    }
  }

  // Handle menu item clicks
  itemClicked(event) {
    if (this.autoCloseValue) {
      // Small delay to allow link navigation to complete
      requestAnimationFrame(() => {
        this.close()
      })
    }
  }
}

